import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import { createFileRoute, useParams } from "@tanstack/react-router"
import { useState } from "react"
import {
  Badge,
  Button,
  ConfirmModal,
  DetailInfoList,
  DetailSidebar,
  FileViewCard,
  formatCurrencyAmount,
  formatDateToDutch,
  LayoutWithSidebar,
  Separator,
  type BadgeVariant,
} from "toptaal-shared"
import { useInvoiceQuery } from "@/api/queries/invoices/useInvoiceQuery"
import { CREDITABLE_STATUSES, InvoiceStatus } from "@/api/types/invoices"
import useFileActions from "@/hooks/useFileActions"
import { useCreditInvoiceMutation } from "@/api/mutations/invoices/useCreditInvoiceMutation"
import { useSendInvoiceToStudentMutation } from "@/api/mutations/invoices/useSendInvoiceToStudentMutation"
import { useFinalizeInvoiceMutation } from "@/api/mutations/invoices/useFinalizeInvoiceMutation"
import {
  getInvoiceLineSubtitle,
  getInvoiceLineTitle,
} from "@/utils/invoiceLineDetails"

export const Route = createFileRoute("/facturatie/facturen/$invoiceId/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "invoices"),
})

const STATUS_BADGE_VARIANT: Record<InvoiceStatus, BadgeVariant> = {
  concept: "lightGrey",
  finalizing: "yellow",
  open: "blue",
  sent_to_student: "yellow",
  waiting_for_duo_export_batch: "yellow",
  waiting_for_duo_approval: "yellow",
  rejected_by_duo: "red",
  sent_to_twinfield: "blue",
  overdue: "red",
  paid: "green",
  credited: "lightGrey",
}

function RouteComponent() {
  const { invoiceId } = useParams({ from: "/facturatie/facturen/$invoiceId/" })
  const { data: invoice } = useInvoiceQuery(Number(invoiceId))
  const { onDownloadClick, onViewClick } = useFileActions("INVOICE")
  const { mutate: creditMutate, isPending: isCrediting } =
    useCreditInvoiceMutation()
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false)
  const sendMutation = useSendInvoiceToStudentMutation()
  const finalizeMutation = useFinalizeInvoiceMutation()

  const canCredit = invoice
    ? CREDITABLE_STATUSES.includes(invoice.status)
    : false
  const invoiceDocument = invoice
    ? invoice.invoiceType === "credit_invoice"
      ? invoice.creditDocument
      : (invoice.signedDocument ?? invoice.document)
    : null
  const isProcessing = invoice?.status === "finalizing"
  const isSubmitting = sendMutation.isPending || finalizeMutation.isPending

  const processInvoice = () => {
    if (!invoice) return

    const mutation =
      invoice.paymentSource === "self" ? finalizeMutation : sendMutation

    mutation.mutate(invoice.id, {
      onSettled: () => setSendConfirmOpen(false),
    })
  }

  return (
    <ScreenWrapper className="!px-0">
      <LayoutWithSidebar
        disableRightPanelSuspense
        sidebar={
          <DetailSidebar
            goBackLabel="Factuur"
            goBackRoute="/facturatie/facturen"
          >
            {invoice && (
              <div className="bg-white rounded-lg p-4 mt-4">
                <p className="font-bold mb-3">
                  {invoice.invoiceNumber
                    ? `Factuur #${invoice.invoiceNumber}`
                    : "Conceptfactuur"}
                </p>

                <Separator className="mb-3" />

                <DetailInfoList
                  items={[
                    { label: "Naam", value: invoice.studentName },
                    {
                      label: "Soort",
                      value: invoice.paymentSourceLabel,
                    },
                    {
                      label: "Datum",
                      value: formatDateToDutch(invoice.invoiceDate),
                    },
                    {
                      label: "Status",
                      value: (
                        <Badge variant={STATUS_BADGE_VARIANT[invoice.status]}>
                          {invoice.statusLabel}
                        </Badge>
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </DetailSidebar>
        }
      >
        {invoice && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-bold">Factuur details</h1>
            </div>

            <div className="border rounded border-primary-accent-blue overflow-hidden mb-6">
              <div className="flex justify-between items-center p-4 font-bold">
                <span>Totaal</span>
                <span>{formatCurrencyAmount(Number(invoice.totalAmount))}</span>
              </div>

              <Separator />

              {invoice.lines
                .slice()
                .sort((a, b) => a.position - b.position)
                .map((line) => (
                  <div key={line.id}>
                    <div className="flex justify-between items-start gap-4 p-4">
                      <div className="min-w-0">
                        <p className="font-bold">{getInvoiceLineTitle(line)}</p>
                        <p>{getInvoiceLineSubtitle(line)}</p>
                      </div>
                      <span className="shrink-0">
                        {formatCurrencyAmount(Number(line.totalAmount))}
                      </span>
                    </div>
                  </div>
                ))}
            </div>

            {invoiceDocument ? (
              <FileViewCard
                id={invoice.id}
                filename={invoiceDocument.filename ?? invoice.invoiceNumber}
                uploadDate={invoice.invoiceDate}
                onDownloadClick={() =>
                  onDownloadClick(
                    invoice.id,
                    invoiceDocument.id,
                    invoiceDocument.filename ?? String(invoice.id),
                  )
                }
                onViewClick={() => onViewClick(invoice.id, invoiceDocument.id)}
              />
            ) : (
              isProcessing && (
                <p className="text-sm text-primary-dark-blue-light">
                  Het factuurdocument wordt gemaakt. Deze pagina wordt
                  automatisch bijgewerkt.
                </p>
              )
            )}

            <div className="flex gap-2 mt-6">
              <ConfirmModal
                open={sendConfirmOpen}
                onOpenChange={setSendConfirmOpen}
                title={
                  invoice.paymentSource === "self"
                    ? "Factuur definitief maken"
                    : "Factuur versturen naar cursist"
                }
                text={
                  invoice.paymentSource === "self"
                    ? "Weet je zeker dat je deze factuur definitief wilt maken?"
                    : "Weet je zeker dat je deze factuur naar de cursist wilt versturen?"
                }
                confirmText={
                  invoice.paymentSource === "self"
                    ? "Definitief maken"
                    : "Versturen"
                }
                cancelText="Annuleren"
                onConfirm={processInvoice}
                trigger={
                  invoice.status === "concept" && (
                    <Button
                      variant="primary"
                      className="mr-2"
                      isLoading={isSubmitting}
                    >
                      {invoice.paymentSource === "self"
                        ? "Factuur definitief maken"
                        : "Factuur versturen naar cursist"}
                    </Button>
                  )
                }
              />

              <ConfirmModal
                title="Factuur crediteren"
                text="Weet je zeker dat je deze factuur wilt crediteren? Er wordt een nieuwe creditfactuur aangemaakt."
                confirmText="Crediteren"
                cancelText="Annuleren"
                onConfirm={() =>
                  !isCrediting && creditMutate(Number(invoiceId))
                }
                trigger={
                  <Button
                    variant="primary"
                    disabled={
                      !canCredit || invoice.invoiceType === "credit_invoice"
                    }
                    isLoading={isCrediting}
                  >
                    Factuur crediteren
                  </Button>
                }
              />
            </div>
          </>
        )}
      </LayoutWithSidebar>
    </ScreenWrapper>
  )
}
